/* ============================================
   WikiArticleViewer — Article Content Renderer
   Renders Markdown → HTML with mermaid diagrams,
   auto-generated TOC, and metadata display.
   ============================================ */

import { useMemo } from 'react'
import { marked } from 'marked'
import { sanitizeHtml } from '@/lib/utils'
import WikiMermaidRenderer from './WikiMermaidRenderer'
import type { WikiArticulo } from '@/hooks/useWiki'

/* ── Inline SVG Icons ── */
const icons = {
  edit: (s = 18) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  ),
  trash: (s = 16) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
    </svg>
  ),
  calendar: (s = 14) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  ),
  list: (s = 16) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" />
      <line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" />
      <line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
    </svg>
  ),
}

interface TocItem {
  level: number
  text: string
  id: string
}

interface WikiArticleViewerProps {
  articulo: WikiArticulo
  onEdit: () => void
  onDelete?: () => void
}

/* ── Extract mermaid blocks and replace with placeholders ── */
function extractMermaidBlocks(markdown: string): {
  cleanedMarkdown: string
  mermaidBlocks: { id: string; chart: string }[]
} {
  const mermaidBlocks: { id: string; chart: string }[] = []
  let counter = 0

  const cleanedMarkdown = markdown.replace(
    /```mermaid\s*\n([\s\S]*?)```/g,
    (_match, chart: string) => {
      const id = `mermaid-block-${counter++}`
      mermaidBlocks.push({ id, chart: chart.trim() })
      return `<div data-mermaid-id="${id}"></div>`
    }
  )

  return { cleanedMarkdown, mermaidBlocks }
}

/* ── Extract TOC from HTML ── */
function extractToc(html: string): TocItem[] {
  const toc: TocItem[] = []
  const regex = /<h([23])\s+id="([^"]*)"[^>]*>(.*?)<\/h[23]>/gi
  let match: RegExpExecArray | null

  while ((match = regex.exec(html)) !== null) {
    toc.push({
      level: parseInt(match[1]),
      text: match[3].replace(/<[^>]*>/g, ''), // Strip nested HTML tags
      id: match[2],
    })
  }

  return toc
}

/* ── Format date ── */
function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('es-PE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

/* ── Status config ── */
const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  publicado: { label: 'Publicado', color: 'var(--success)', bg: 'var(--success-bg)' },
  borrador: { label: 'Borrador', color: 'var(--warning)', bg: 'var(--warning-bg)' },
  archivado: { label: 'Archivado', color: 'var(--text-muted)', bg: 'color-mix(in srgb, var(--text-muted) 12%, transparent)' },
}

export default function WikiArticleViewer({ articulo, onEdit, onDelete }: WikiArticleViewerProps) {
  /* ── Parse markdown to HTML ── */
  const { html, mermaidBlocks, toc } = useMemo(() => {
    const { cleanedMarkdown, mermaidBlocks } = extractMermaidBlocks(articulo.contenido)

    // Configure marked with heading IDs
    const renderer = new marked.Renderer()
    renderer.heading = function ({ text, depth }: { text: string; depth: number }) {
      const slug = text
        .toLowerCase()
        .replace(/<[^>]*>/g, '')
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim()

      return `<h${depth} id="${slug}">${text}</h${depth}>`
    }

    // Make links open in new tab
    renderer.link = function ({ href, title, text }: { href: string; title?: string | null; text: string }) {
      const titleAttr = title ? ` title="${title}"` : ''
      return `<a href="${href}"${titleAttr} target="_blank" rel="noopener noreferrer">${text}</a>`
    }

    const rawHtml = marked.parse(cleanedMarkdown, {
      renderer,
      gfm: true,
      breaks: true,
    }) as string

    const sanitized = sanitizeHtml(rawHtml)
    const tocItems = extractToc(sanitized)

    return { html: sanitized, mermaidBlocks, toc: tocItems }
  }, [articulo.contenido])

  const status = STATUS_CONFIG[articulo.estado] || STATUS_CONFIG.borrador

  /* ── Render content with mermaid placeholders replaced ── */
  const renderContent = () => {
    if (mermaidBlocks.length === 0) {
      return (
        <div
          className="wiki-article__content wiki-prose"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      )
    }

    // Split HTML by mermaid placeholders and interleave mermaid components
    const parts: React.ReactNode[] = []
    let remaining = html
    let partIndex = 0

    for (const block of mermaidBlocks) {
      const placeholder = `<div data-mermaid-id="${block.id}"></div>`
      const idx = remaining.indexOf(placeholder)

      if (idx !== -1) {
        // Add HTML before placeholder
        if (idx > 0) {
          parts.push(
            <div
              key={`html-${partIndex}`}
              className="wiki-prose"
              dangerouslySetInnerHTML={{ __html: remaining.substring(0, idx) }}
            />
          )
        }

        // Add mermaid component
        parts.push(
          <WikiMermaidRenderer key={`mermaid-${block.id}`} chart={block.chart} id={block.id} />
        )

        remaining = remaining.substring(idx + placeholder.length)
        partIndex++
      }
    }

    // Add remaining HTML
    if (remaining.trim()) {
      parts.push(
        <div
          key={`html-${partIndex}`}
          className="wiki-prose"
          dangerouslySetInnerHTML={{ __html: remaining }}
        />
      )
    }

    return <div className="wiki-article__content">{parts}</div>
  }

  return (
    <article className="wiki-article">
      {/* ── Header ── */}
      <header className="wiki-article__header">
        <div className="wiki-article__header-top">
          {articulo.categoria && (
            <span
              className="wiki-article__category-badge"
              style={{
                borderColor: articulo.categoria.color || 'var(--primary-light)',
                color: articulo.categoria.color || 'var(--primary-light)',
              }}
            >
              {articulo.categoria.nombre}
            </span>
          )}
          <span
            className="wiki-article__status-badge"
            style={{ color: status.color, background: status.bg }}
          >
            {status.label}
          </span>
        </div>

        <h1 className="wiki-article__title">{articulo.titulo}</h1>

        {articulo.resumen && (
          <p className="wiki-article__summary">{articulo.resumen}</p>
        )}

        <div className="wiki-article__meta">
          <span className="wiki-article__meta-item">
            {icons.calendar()}
            {formatDate(articulo.updated_at)}
          </span>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button className="wiki-article__edit-btn" onClick={onEdit} title="Editar artículo">
              {icons.edit()} Editar
            </button>
            {onDelete && (
              <button
                className="wiki-article__edit-btn"
                onClick={onDelete}
                title="Eliminar artículo"
                style={{ color: 'var(--danger)', borderColor: 'color-mix(in srgb, var(--danger) 30%, transparent)' }}
              >
                {icons.trash(16)} Eliminar
              </button>
            )}
          </div>
        </div>
      </header>

      {/* ── Table of Contents ── */}
      {toc.length > 2 && (
        <div className="wiki-article__toc">
          <div className="wiki-article__toc-title">
            {icons.list()} Contenido
          </div>
          <ul className="wiki-article__toc-list">
            {toc.map((item, i) => (
              <li
                key={i}
                className={`wiki-article__toc-item wiki-article__toc-item--h${item.level}`}
              >
                <a
                  href={`#${item.id}`}
                  onClick={e => {
                    e.preventDefault()
                    document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' })
                  }}
                >
                  {item.text}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Content ── */}
      {renderContent()}
    </article>
  )
}
